import { NotFound } from "@/components/shared/NotFound";
import { CanvasSkeleton } from "@/components/skeletons";
import { SlideEditorProvider } from "@/components/slides/editor/SlideEditorContext";
import { AppButton } from "@/components/teacher/AppButton";
import { TeacherPage } from "@/components/teacher/TeacherPage";
import { format, useT } from "@/i18n";
import { useGetLiveMobileView } from "@/lib/hooks/slides/editor/useGetLiveMobileView";
import { useSlideById } from "@/lib/hooks/slides/useSlideById";
import { useSlidesByLessonId } from "@/lib/hooks/slides/useSlidesByLessonId";
import { toPlayOrder } from "@/lib/slides/playOrder";
import { convertElementsToPixels } from "@/lib/utils/elementConversion";
import { hasGraphQLErrorCode } from "@/lib/utils/graphqlErrors";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { loadSlide } from "@/store/slideEditorSlice";
import type { SlideFields } from "@/types/Slide";
import { ErrorCodes, resolveSlideTemplate } from "@ai-teach/types";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { ActionCreators } from "redux-undo";

interface TeacherSlidePageProps {
  lessonId: string;
  slideId: string;
}

function TeacherSlidePageContent({ lessonId, slideId }: TeacherSlidePageProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const t = useT();
  const loadedSlideIdRef = useRef<string | null>(null);

  const { slide, error } = useSlideById({ slideId });

  // The lesson comes from the cache on the way in from its page, and is fetched on a deep link.
  const { lessonSlideItems } = useSlidesByLessonId({ lessonId });
  const play = toPlayOrder(lessonSlideItems);
  const index = play.findIndex((item) => item.id === slideId);
  const previous = index > 0 ? play[index - 1] : undefined;
  const next = index >= 0 ? play[index + 1] : undefined;

  // The live view reads the open slide from the editor store, so it has to be loaded there first.
  useEffect(() => {
    if (!slide || loadedSlideIdRef.current === slide.id) return;

    dispatch(
      loadSlide({
        ...slide,
        elements: convertElementsToPixels(slide.elements, resolveSlideTemplate(slide)),
      } as SlideFields)
    );
    dispatch(ActionCreators.clearHistory());
    loadedSlideIdRef.current = slide.id;
  }, [slide, dispatch]);

  const openSlideId = useAppSelector((state) => state.slideEditor.present.slide?.id);
  const liveView = useGetLiveMobileView();
  // Until the store holds THIS slide, the live view would still be rendering the previous one.
  const isReady = openSlideId === slideId;

  const goToSlide = (id: string) =>
    navigate({
      to: "/teacher/books/lessons/$lessonId/slides/$slideId",
      params: { lessonId, slideId: id },
    });

  if (hasGraphQLErrorCode(error, ErrorCodes.NOT_FOUND)) {
    return <NotFound title={t["slide.notFoundTitle"]} description={t["slide.notFoundBody"]} />;
  }

  return (
    <TeacherPage
      title={slide?.name ?? t["slide.fallbackName"]}
      subtitle={
        index >= 0
          ? format(t["slide.position"], { current: index + 1, total: play.length })
          : undefined
      }
      actions={
        <>
          <AppButton
            tone="glass"
            className="px-3 sm:px-5"
            aria-label={t["slide.previous"]}
            disabled={!previous}
            onClick={() => previous && goToSlide(previous.id)}
          >
            <HiChevronLeft className="h-5 w-5" aria-hidden="true" />
          </AppButton>
          <AppButton
            tone="glass"
            className="px-3 sm:px-5"
            aria-label={t["slide.next"]}
            disabled={!next}
            onClick={() => next && goToSlide(next.id)}
          >
            <HiChevronRight className="h-5 w-5" aria-hidden="true" />
          </AppButton>
          <AppButton
            tone="glass"
            onClick={() =>
              navigate({ to: "/teacher/books/lessons/$lessonId", params: { lessonId } })
            }
          >
            {t["common.back"]}
          </AppButton>
        </>
      }
    >
      {/* The frame is the app's own 480×800; narrow viewports scroll it rather than squash it. */}
      <div className="app-card flex justify-center overflow-x-auto p-3 sm:p-6">
        {isReady ? liveView : <CanvasSkeleton canvasType="freeform" />}
      </div>
    </TeacherPage>
  );
}

/** One slide exactly as a student meets it — the live view, with nothing to edit. */
export function TeacherSlidePage(props: TeacherSlidePageProps) {
  return (
    <SlideEditorProvider>
      <TeacherSlidePageContent {...props} />
    </SlideEditorProvider>
  );
}
